import React, { useEffect, useMemo, useState } from 'react';
import { AutoAwesome, Close, Tune as TuneIcon } from '@mui/icons-material';

import Tooltip from '../utils/Tooltip';
import GenerationRunControl from '../generator/GenerationRunControl';
import GeneratorControlBar from '../generator/GeneratorControlBar';
import GenConfPanel from '../generator/GenConfPanel';
import DefenseShareControl from '../generator/DefenseShareControl';
import GenTimer from '../generator/GenTimer';
import '../generator/GenTimer.css';
import GenConfig from '../../core/GenConfig';
import { AP_ID, MP_ID, RANGE_ID } from '../../core/st';
import { fmExosFromShortcuts } from '../../core/itemMaging';
import { useOverlay } from '../../contexts/OverlayContext';
import { getCharacterClass } from '../../core/characterUtils';
import SpellService from '../../services/SpellService';
import { getTypeId } from '../../core/spellUtils';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { INFINITY, t, statDefById, localizedStatAlias, sortSpellChipsByElement } from './aiChatUtils';
import './AiChatDraftChips.css';

const TXT_GENERATOR = { fr: 'générateur', en: 'generator', de: 'Generator', es: 'generador', pt: 'gerador' };

const GENERIC_SPELL_ICON_SRC = '/img/spells.png';
const TXT_GEN_SETTINGS = { fr: 'Paramètres du générateur', en: 'Generator settings', de: 'Generator-Einstellungen', es: 'Ajustes del generador', pt: 'Configurações do gerador' };
const TXT_EXOS = { fr: 'Exos', en: 'Exos', de: 'Exos', es: 'Exos', pt: 'Exos' };
const TXT_SPELLS = { fr: 'Sorts', en: 'Spells', de: 'Zauber', es: 'Hechizos', pt: 'Feitiços' };

function AiChatGeneratorHeader({ lang }) {
    return (
        <div className="ai-chat-generator-header row">
            <AutoAwesome fontSize="small" />
            <h6>{TXT_GENERATOR[lang] || TXT_GENERATOR.en}</h6>
        </div>
    );
}

export default function AiChatDraftChips({
    draft,
    isActiveDraft,
    chipsDisabled,
    timeLimit,
    onLevelChange,
    onStatChange,
    onStatRemove,
    onSpellRemove,
    onExoChange,
    onTimeLimitChange,
    onGenConfigChange,
    generationJob,
    interpreting,
    openBusy,
    onGenerate,
    onStop,
    lang,
}) {
    const statsChips = useMemo(
        () => draft?.draft_metadata?.criteria_labels?.stats || [],
        [draft?.draft_metadata?.criteria_labels?.stats],
    );
    const spellChips = useMemo(
        () => draft?.draft_metadata?.criteria_labels?.spells || [],
        [draft?.draft_metadata?.criteria_labels?.spells],
    );
    const [spellDataById, setSpellDataById] = useState(new Map());
    const { showOverlay } = useOverlay();
    const hasTargets = statsChips.length + spellChips.length > 0;
    const classTypeId = useMemo(() => {
        const classSlug = String(draft?.draft_metadata?.characterClass || '').toLowerCase();
        return getTypeId(classSlug);
    }, [draft?.draft_metadata?.characterClass]);
    const characterClass = useMemo(() => {
        const classSlug = String(draft?.draft_metadata?.characterClass || '').toLowerCase();
        return getCharacterClass(classSlug) || null;
    }, [draft?.draft_metadata?.characterClass]);
    const classLabel = characterClass?.label?.[lang]
        || characterClass?.label?.en
        || draft?.draft_metadata?.characterClass
        || '';
    const levelValue = Number(draft?.req?.lvl) || '';
    const exos = draft?.req?.exos || null;
    // The exos the draft's generation places, read as aiDraftToBuild reads them.
    const placedExos = fmExosFromShortcuts(exos);
    const handleExoToggle = (id) => {
        if (chipsDisabled) return;
        onExoChange?.(id, placedExos.includes(id) ? 0 : 1);
    };
    const genConfig = useMemo(() => new GenConfig({
        ...(draft?.req?.settings || {}),
        defenseShare: draft?.req?.defense_share,
        timeLimit,
    }), [draft?.req?.defense_share, draft?.req?.settings, timeLimit]);
    const setDraftGenConfig = (updater) => {
        const nextConfig = typeof updater === 'function' ? updater(genConfig) : updater;
        onGenConfigChange?.(nextConfig);
    };

    useEffect(() => {
        let cancelled = false;
        if (!classTypeId || spellChips.length === 0) {
            setSpellDataById(new Map());
            return () => { cancelled = true; };
        }

        SpellService.getClassSpells(classTypeId)
            .then((data) => {
                if (cancelled) return;
                const nextMap = new Map();
                const allSpells = [
                    ...(Array.isArray(data?.spells) ? data.spells : []),
                    ...(Array.isArray(data?.other) ? data.other : []),
                ];
                allSpells.forEach((spell) => {
                    const spellId = Number(spell?.id);
                    if (!Number.isFinite(spellId)) return;
                    nextMap.set(spellId, spell);
                });
                setSpellDataById(nextMap);
            })
            .catch(() => {
                if (!cancelled) setSpellDataById(new Map());
            });

        return () => { cancelled = true; };
    }, [classTypeId, spellChips]);

    const renderDivider = (key) => (
        <span key={key} className="ai-chat-chip-divider" aria-hidden="true">•</span>
    );
    const interleaveDividers = (nodes, keyPrefix) =>
        nodes.flatMap((node, i) => (i === 0 ? [node] : [renderDivider(`${keyPrefix}-div-${i}`), node]));

    const metaChips = [
        classLabel ? (
            <span key="meta-class" className="ai-chat-chip ai-chat-generator-meta-chip">
                {characterClass?.icon ? <img src={characterClass.icon} alt="" /> : null}
                <span className="ai-chat-chip-label">{classLabel}</span>
            </span>
        ) : null,
        <span key="meta-level" className="ai-chat-chip ai-chat-generator-meta-chip ai-chat-generator-meta-chip--level">
            <span className="ai-chat-chip-label">{t(lang, 'level')}</span>
            <input
                type="number"
                className="ai-chat-chip-input"
                min={1}
                max={200}
                size={Math.max(1, String(levelValue || '').length)}
                value={levelValue}
                onChange={(e) => onLevelChange?.(e.target.value)}
                disabled={chipsDisabled}
            />
        </span>,
    ].filter(Boolean);

    const criteriaMetaContent = (
        <div className="ai-chat-generator-meta-list row">
            {interleaveDividers(metaChips, 'meta')}
        </div>
    );

    const exoContent = (
        <div className="ai-chat-exo-row">
            <span className="ai-chat-exo-row-label">{TXT_EXOS[lang] || TXT_EXOS.en}</span>
            <span className="ai-chat-exo-group ai-chat-criteria-row-items">
                {[AP_ID, MP_ID, RANGE_ID].map((id) => {
                    const stat = statDefById(id);
                    const on = placedExos.includes(id);
                    return (
                        <span key={`exo-${id}`} className="ai-chat-chip ai-chat-exo-chip">
                            <span className="ai-chat-exo-alias">{localizedStatAlias(stat, lang)}</span>
                            <button
                                type="button"
                                className={`ai-chat-exo-button${on ? ' active' : ''}`}
                                onClick={() => handleExoToggle(id)}
                                disabled={chipsDisabled}
                            >
                                {on ? '+1' : '+0'}
                            </button>
                        </span>
                    );
                })}
            </span>
        </div>
    );

    // Stats criteria displayed in st.js definition order.
    const orderedStatsChips = [...statsChips].sort(
        (a, b) => (statDefById(a.id)?.order ?? 999) - (statDefById(b.id)?.order ?? 999)
    );
    // Spell criteria grouped by element (earth, fire, water, air, then pushback), each by level.
    const orderedSpellChips = sortSpellChipsByElement(
        spellChips,
        spellDataById,
        Number(draft?.req?.lvl) || 200,
    );
    // Beyond this many spells, show icons only with the name on hover.
    const iconOnlySpells = spellChips.length > 10;

    const renderRemovableIcon = ({ icon, fallback, onRemove, removeLabel }) => (
        <button
            type="button"
            className="ai-chat-criteria-remove"
            onClick={onRemove}
            disabled={chipsDisabled || !onRemove}
            aria-label={removeLabel}
        >
            <span className="ai-chat-criteria-remove__slot">
                {icon ? (
                    <img className="ai-chat-criteria-remove__icon" src={icon} alt="" />
                ) : (
                    <span className="ai-chat-criteria-remove__icon ai-chat-criteria-remove__fallback">
                        {fallback}
                    </span>
                )}
                <Close className="ai-chat-criteria-remove__close" fontSize="small" />
            </span>
        </button>
    );

    const criteriaContent = (
        <div className={`ai-chat-criteria-details${chipsDisabled ? ' ai-chat-chips--disabled' : ''}`}>
            {criteriaMetaContent}
            {orderedStatsChips.length > 0 ? (
                <div className="ai-chat-stats-row">
                    <span className="ai-chat-exo-row-label">{t(lang, 'statsCriteria')}</span>
                    <div className="ai-chat-criteria-row-items">
                        {orderedStatsChips.map((row) => {
                            const stat = statDefById(row.id);
                            const icon = stat?.icon;
                            return (
                                <span key={`stat-${row.id}`} className="ai-chat-chip ai-chat-stat-chip">
                                    {renderRemovableIcon({
                                        icon,
                                        fallback: <AutoAwesome fontSize="inherit" />,
                                        onRemove: () => onStatRemove?.(row.id),
                                        removeLabel: `${t(lang, 'remove')} ${row.label}`,
                                    })}
                                    {row.maximize ? (
                                        <span className="ai-chat-chip-infinity ai-chat-chip-value">{INFINITY}</span>
                                    ) : (
                                        <input
                                            type="number"
                                            className="ai-chat-chip-input ai-chat-chip-value"
                                            min={0}
                                            max={9999}
                                            size={Math.max(1, String(Number(row.value) || '').length)}
                                            value={Number(row.value) || ''}
                                            onChange={(e) => onStatChange(row.id, e.target.value)}
                                            disabled={chipsDisabled}
                                        />
                                    )}
                                    <span className="ai-chat-chip-label">{row.label}</span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            ) : null}
            {exoContent}
            {spellChips.length > 0 ? (
                <div className="ai-chat-spells-row">
                    <span className="ai-chat-exo-row-label">{TXT_SPELLS[lang] || TXT_SPELLS.en}</span>
                    <div className="ai-chat-criteria-row-items">
                        {orderedSpellChips.map((row) => {
                            const spellId = Number(row?.spell_id ?? row?.spellId ?? row?.id);
                            const icon = resolveMediaUrl(spellDataById.get(spellId)?.icon_url)
                                || GENERIC_SPELL_ICON_SRC;
                            const removable = renderRemovableIcon({
                                icon,
                                fallback: <AutoAwesome fontSize="inherit" />,
                                onRemove: () => onSpellRemove?.(spellId),
                                removeLabel: `${t(lang, 'remove')} ${row.name || ''}`.trim(),
                            });
                            return (
                                <span
                                    key={`spell-${spellId}`}
                                    className={`ai-chat-chip ai-chat-spell-chip${iconOnlySpells ? ' ai-chat-spell-chip--icon-only' : ''}`}
                                >
                                    {iconOnlySpells ? (
                                        <Tooltip content={row.name}>{removable}</Tooltip>
                                    ) : (
                                        <>
                                            {removable}
                                            <span className="ai-chat-chip-label">{row.name}</span>
                                        </>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );

    const runControlContent = (
        <div className="ai-chat-chip-action">
            <GenerationRunControl
                status={generationJob.status}
                lang={lang}
                timeLimit={timeLimit}
                onGenerate={onGenerate}
                onStop={onStop}
                disabled={interpreting || openBusy}
                hasTargets={hasTargets}
                showStatus={false}
                className="ai-chat-generation-control"
                buttonClassName="ai-chat-generation-control__button"
            />
        </div>
    );

    const settingsContent = (
        <button
            className="settings-btn row"
            type="button"
            onClick={() => {
                showOverlay([
                    <h6>{TXT_GEN_SETTINGS[lang] || TXT_GEN_SETTINGS.en}</h6>,
                    <GenConfPanel
                        lang={lang}
                        genConfig={genConfig}
                        setGenConfig={setDraftGenConfig}
                    />,
                ]);
            }}
            disabled={chipsDisabled}
            aria-label={TXT_GEN_SETTINGS[lang] || TXT_GEN_SETTINGS.en}
        >
            <TuneIcon fontSize="medium" />
        </button>
    );

    return (
        <div className="ai-chat-draft-bars">
            <div className={`ai-chat-criteria-translation-bar${chipsDisabled ? ' ai-chat-generator-bar--disabled' : ''}`}>
                {criteriaContent}
            </div>
            {isActiveDraft ? (
                <GeneratorControlBar
                    variant="aichat"
                    isBusy={generationJob.isGenerating}
                    titleContent={<AiChatGeneratorHeader lang={lang} />}
                    genModeControls={(
                        <DefenseShareControl
                            lang={lang}
                            defenseShare={genConfig.defenseShare}
                            onDefenseShareChange={(share) => setDraftGenConfig(genConfig.update('defenseShare', share))}
                            disabled={chipsDisabled}
                        />
                    )}
                    settingsContent={settingsContent}
                    timerContent={(
                        <GenTimer
                            value={timeLimit}
                            onChange={onTimeLimitChange}
                            min={GenConfig.TIME_LIMIT_MIN}
                            steps={GenConfig.TIME_LIMIT_STEPS}
                            max={GenConfig.TIME_LIMIT_MAX}
                            step={GenConfig.TIME_LIMIT_STEP}
                            disabled={chipsDisabled}
                            lang={lang}
                        />
                    )}
                    busyStatusContent={(
                        <GenerationRunControl
                            status={generationJob.status}
                            lang={lang}
                            timeLimit={timeLimit}
                            onGenerate={onGenerate}
                            onStop={onStop}
                            disabled={interpreting || openBusy}
                            hasTargets={hasTargets}
                            showButton={false}
                            className="ai-chat-generation-control"
                        />
                    )}
                    runControlContent={runControlContent}
                    showScore={false}
                    className={chipsDisabled ? 'ai-chat-generator-bar--disabled' : ''}
                />
            ) : null}
        </div>
    );
}
